import { ControlledFormCurrencyInput } from '@components/formInputs/ControlledFormCurrencyInput';
import { ControlledFormSelect } from '@components/formInputs/ControlledFormSelect';
import { LayoutVariant } from '@components/formInputs/sharedTypes';
import { Icon } from '@components/icon/Icon';
import { useFormContext, useWatch } from 'react-hook-form';
import { type AdminRestaurant } from '../../team/apiHelpers';
import { VALID_HOURS } from '../utils/cancellationPolicyUtils';
import { SettingsOutletHeader } from '../../shared/SettingsOutletHeader';
import {
  cancellationFeeFieldRules,
  cancellationThresholdFieldRules,
} from '../utils/cancellationPolicyFieldRules';
import { YES_NO_SELECT_OPTIONS } from '../utils/generalSettingsFormHelpers';
import { type GeneralSettingsForm } from '../utils/generalSettingsFormTypes';
import layoutStyles from './CancellationPolicySection.scss';

const CANCELLATION_POLICY_SECTION_PRIMARY =
  'Set the cancellation policy for guests. Guests canceling reservations at or below the threshold price within the set time period will be subject to the cancellation fee at your discretion.';
const CANCELLATION_POLICY_SECTION_SECONDARY =
  'Admins will always have the final say on whether to charge or waive the fee, up to 72 hours after the reservation.';

export interface CancellationPolicySectionProps {
  restaurant: AdminRestaurant;
  sectionTitleId: string;
  isSaving: boolean;
  onRequestRemovePolicy: () => void;
}

export const CancellationPolicySection = ({
  restaurant,
  sectionTitleId,
  isSaving,
  onRequestRemovePolicy,
}: CancellationPolicySectionProps) => {
  const { control } = useFormContext<GeneralSettingsForm>();
  const watchedCancellationPolicyEnabled = useWatch({
    control,
    name: 'cancellationPolicyEnabled',
  });

  return (
    <section className={layoutStyles.section} aria-labelledby={sectionTitleId}>
      <SettingsOutletHeader
        variant="section"
        titleId={sectionTitleId}
        title="Cancellation Policy"
      />
      <p className={layoutStyles.sectionSubtext}>
        {CANCELLATION_POLICY_SECTION_PRIMARY}
      </p>
      <p className={layoutStyles.sectionSubtext}>
        {CANCELLATION_POLICY_SECTION_SECONDARY}
      </p>
      <div className={layoutStyles.formFields}>
        <ControlledFormSelect
          control={control}
          label="Use cancellation policy"
          name="cancellationPolicyEnabled"
          options={YES_NO_SELECT_OPTIONS}
          rules={{ required: true }}
          variant={LayoutVariant.Vertical}
          className={layoutStyles.constrainedField}
        />
        {watchedCancellationPolicyEnabled === 1 ? (
          <>
            <ControlledFormCurrencyInput
              control={control}
              integerDollarsOnly
              label="Fee"
              name="cancellationFee"
              placeholder="Enter fee"
              rules={cancellationFeeFieldRules}
              variant={LayoutVariant.Vertical}
              className={layoutStyles.constrainedField}
            />
            <ControlledFormSelect
              control={control}
              label="Time before fees apply"
              name="cancellationHoursBeforeReservation"
              options={VALID_HOURS}
              rules={{ required: true }}
              variant={LayoutVariant.Vertical}
              className={layoutStyles.constrainedField}
            />
            <ControlledFormCurrencyInput
              control={control}
              integerDollarsOnly
              label="Price threshold"
              name="cancellationThreshold"
              rules={cancellationThresholdFieldRules}
              variant={LayoutVariant.Vertical}
              className={layoutStyles.constrainedField}
            />
          </>
        ) : null}
        {restaurant.cancellationPolicy != null &&
        watchedCancellationPolicyEnabled === 1 ? (
          <button
            type="button"
            className={layoutStyles.removeButton}
            disabled={isSaving}
            aria-label="Remove cancellation policy"
            onClick={onRequestRemovePolicy}
          >
            <Icon ariaLabel="Remove cancellation policy" name="trash" />
            <span className={layoutStyles.removeLabel}>Remove Policy</span>
          </button>
        ) : null}
      </div>
    </section>
  );
};
